import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Upload, X, FileImage } from 'lucide-react'
import { cn, validateImage, getErrorMessage } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { useCreateArtwork, useUploadArtworkImage } from '../hooks/useArtworks'
import { artworkApi } from '../api/artworkApi'
import { Progress } from '@/components/ui/progress'

// The File is held, not extended: its name, size and type are getters on File's prototype, so
// spreading one (`{ ...file, status }`) to update its state kept none of them. The list then
// showed "NaN MB", and "Try the failed files again" sent an object with no name or bytes.
interface UploadItem {
  file: File
  preview?: string
  artworkId?: number
  progress?: number
  status?: 'pending' | 'uploading' | 'done' | 'error'
  error?: string
}

export function UploadPage() {
  const [files, setFiles] = useState<UploadItem[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { mutateAsync: createArtwork } = useCreateArtwork()
  const { mutateAsync: uploadImage } = useUploadArtworkImage()

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const validatedFiles = acceptedFiles.filter((file) => {
        const result = validateImage(file)
        if (!result.valid) {
          toast({
            title: 'Invalid file',
            description: `${file.name}: ${result.error}`,
            variant: 'destructive',
          })
          return false
        }
        return true
      })

      const filesWithPreview = validatedFiles.map((file): UploadItem => ({
        file,
        preview: URL.createObjectURL(file),
        status: 'pending',
        progress: 0,
      }))

      setFiles((prev) => [...prev, ...filesWithPreview])
    },
    [toast]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    multiple: true,
  })

  const removeFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev]
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!)
      }
      newFiles.splice(index, 1)
      return newFiles
    })
  }

  const handleUpload = async () => {
    if (files.length === 0) return

    setIsProcessing(true)

    let failed = 0
    for (let i = 0; i < files.length; i++) {
      const { file, status } = files[i]
      // Retrying a failed file must not make another artwork for it.
      if (status === 'done') continue
      let createdId: number | undefined

      try {
        // Update status to uploading
        setFiles((prev) => {
          const newFiles = [...prev]
          newFiles[i] = { ...newFiles[i], status: 'uploading' }
          return newFiles
        })

        // Create artwork
        const artwork = await createArtwork({
          name: file.name.replace(/\.[^/.]+$/, ''),
        })
        createdId = artwork.id

        // Upload image with progress
        await uploadImage({
          artworkId: artwork.id,
          file,
          onProgress: (progress) => {
            setFiles((prev) => {
              const newFiles = [...prev]
              newFiles[i] = { ...newFiles[i], progress }
              return newFiles
            })
          },
        })

        // Mark as done
        setFiles((prev) => {
          const newFiles = [...prev]
          newFiles[i] = {
            ...newFiles[i],
            status: 'done',
            artworkId: artwork.id,
            progress: 100,
          }
          return newFiles
        })
      } catch (error) {
        failed++
        // An artwork whose image never arrived is an empty draft the artist then has to find
        // and delete (and could even send for review). Take it back out.
        if (createdId) {
          try {
            await artworkApi.deleteArtwork(createdId)
          } catch {
            /* the draft stays; it can be deleted from My Art */
          }
        }
        setFiles((prev) => {
          const newFiles = [...prev]
          newFiles[i] = { ...newFiles[i], status: 'error', error: getErrorMessage(error) }
          return newFiles
        })
      }
    }

    setIsProcessing(false)

    // Stay on the page when something failed, so the reason can be read and the upload retried.
    if (failed === 0) {
      setTimeout(() => {
        navigate('/app/artworks')
      }, 1500)
    }
  }

  const completedCount = files.filter((f) => f.status === 'done').length

  return (
    <div className="space-y-8 p-8">
      <div className="space-y-1">
        <h1 className="font-serif text-4xl font-semibold tracking-tight">Upload Art</h1>
        <p className="text-muted-foreground text-base">
          Upload your high-resolution artwork files
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Select Files</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={cn(
              'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors',
              isDragActive
                ? 'border-primary bg-primary/10'
                : 'border-muted-foreground/25 hover:border-primary'
            )}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">
              {isDragActive ? 'Drop files here' : 'Drag and drop files here'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">or</p>
            <Button type="button">Select to Upload</Button>
          </div>

          {/* Requirements */}
          <div className="space-y-2 text-sm text-muted-foreground">
            <h3 className="font-semibold text-foreground">Some things to consider:</h3>
            <ul className="space-y-1 list-disc list-inside">
              <li>Upload the highest resolution file as JPG or PNG</li>
              <li>Max file size is 200 MB per file</li>
              <li>You must own all rights to the images uploaded</li>
            </ul>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div>
              <h3 className="font-semibold mb-4">
                Selected Files ({completedCount}/{files.length})
              </h3>
              <div className="space-y-3">
                {files.map(({ file, ...item }, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-4 border rounded-lg"
                  >
                    {item.preview ? (
                      <img
                        src={item.preview}
                        alt={file.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <FileImage className="w-16 h-16 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                      {item.status === 'uploading' && (
                        <Progress value={item.progress || 0} className="mt-2" />
                      )}
                      {item.status === 'done' && (
                        <p className="text-xs text-green-600 mt-1">✓ Uploaded</p>
                      )}
                      {item.status === 'error' && (
                        <p className="text-xs text-destructive mt-1">
                          ✗ Failed{item.error ? `: ${item.error}` : ''}
                        </p>
                      )}
                    </div>
                    {item.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upload Button */}
          {files.length > 0 && !isProcessing && completedCount < files.length && (
            <Button onClick={handleUpload} className="w-full" size="lg">
              {files.some((f) => f.status === 'error')
                ? 'Try the failed files again'
                : `Upload ${files.length} file${files.length > 1 ? 's' : ''}`}
            </Button>
          )}

          {isProcessing && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">
                Uploading... {completedCount} of {files.length} complete
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

